import { useReducer, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { MODAL_RESPONSE_TYPE } from 'bxcommon/constants/modal.constants';
import { BaseIcon, BaseTable } from 'bxcommon';
import reducer, { initialState, REDUCER_TYPES } from 'bxcommon/components/ListViewWrapper/ListViewWrapper.reducer';
import { ModalContext } from 'bxcommon/context/Modal.context';

import { ProspectusHeader } from '../ProspectusHeader/ProspectusHeader';
import { useDeleteUserGroupMutation, useGetUserGroupsQuery } from '../../store/endpoints/prospectus.endpoints';

import './UserGroupsList.scss';

export const UserGroupsList = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { data: userGroups = [] } = useGetUserGroupsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [deleteUserGroup] = useDeleteUserGroupMutation();

  const { openConfirmModal } = useContext(ModalContext);
  const [{ listLoading, deleteLoading, activeItemIndex }, updateState] = useReducer(reducer, initialState);

  const goToEdit = useCallback(
    (id) => {
      navigate(`/groups/edit/${id}`);
    },
    [navigate]
  );

  const showSpinner = useCallback(
    (index) => {
      return deleteLoading && index === activeItemIndex;
    },
    [deleteLoading, activeItemIndex]
  );

  const deleteRequest = async (id, index) => {
    const response = await openConfirmModal(
      t('userGroups.cancelModal.title'),
      t('userGroups.cancelModal.description'),
      { okButton: t('userGroups.cancelModal.okButton') }
    );
    if (response === MODAL_RESPONSE_TYPE.ACCEPT) {
      try {
        updateState({ type: REDUCER_TYPES.SET_ACTIVE_ITEM_INDEX, payload: index });
        updateState({ type: REDUCER_TYPES.SET_DELETE_LOADING, payload: true });

        await deleteUserGroup(id).unwrap();
      } finally {
        updateState({ type: REDUCER_TYPES.SET_ACTIVE_ITEM_INDEX, payload: null });
        updateState({ type: REDUCER_TYPES.SET_DELETE_LOADING, payload: false });
      }
    }
  };

  return (
    <ProspectusHeader>
      <div className="container">
        <div className="row">
          <div className="col-12">
            <div className="user-groups">
              <h1 className="heading heading--secondary" data-testid="user-groups-view-heading">
                {t('userGroups.heading')}
              </h1>
            </div>
            {userGroups.length ? (
              <BaseTable labels={t('userGroups.labels', { returnObjects: true })} loading={listLoading}>
                {userGroups.map((item, index) => (
                  <tr key={item.id} data-testid="user-groups-list-item">
                    <td className="copy copy--color-dark user-groups__name" data-testid="user-groups-group-name">
                      {item.name}
                    </td>
                    <td className="copy copy--color-dark" data-testid="user-groups-no-of-members">
                      {item.members.length}
                    </td>
                    <td>
                      <div className="user-groups__controls">
                        <BaseIcon
                          clickable
                          onClick={() => deleteRequest(item.id, index)}
                          icon="delete"
                          data-testid="user-groups-delete-button"
                          loading={showSpinner(index)}
                        />
                        <BaseIcon
                          clickable
                          onClick={() => goToEdit(item.id)}
                          icon="edit"
                          data-testid="user-groups-edit-button"
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </BaseTable>
            ) : (
              <div className="user-groups__empty" data-testid="user-groups-empty">
                {t('userGroups.empty')}
              </div>
            )}
          </div>
        </div>
      </div>
    </ProspectusHeader>
  );
};

export default UserGroupsList;
